import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * The school picker used across the superuser console.
 *
 * The search field is lifted from the embed's .hc-find-school-search so the one
 * admins use reads the same as the one students see: 56px pill, 12px radius, a
 * #C0C0C0 magnifier and a borderless input inside it.
 *
 * @param {object[]} schools
 * @param {object|null} value the selected school
 * @param {function} onChange receives the school
 * @param {string} [placeholder]
 */

const searchBox = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  height: '56px',
  background: '#fff',
  padding: '0 14px',
  gap: '8px',
  boxSizing: 'border-box',
};

const searchIcon = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
};

const searchInput = {
  flex: '1 1 auto',
  minWidth: 0,
  width: '100%',
  height: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontFamily: '"Baikal Book"',
  fontSize: '15px',
  lineHeight: '20px',
  color: '#000',
  padding: 0,
  margin: 0,
  appearance: 'none',
};

const MagnifierIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="7" stroke="#C0C0C0" strokeWidth="1.5" />
    <path d="M20 20L16.5 16.5" stroke="#C0C0C0" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const FILTER_CHEVRON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M7 10L12 15L17 10' stroke='%236b7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")";

// Same closed box as SchoolPicker `size`. Native <select> appearance draws a
// different height than a <button>, so the neighbors have to opt out of it.
export const inlineSelectStyle = (size) => {
  const sm = size !== 'md';
  return {
    height: sm ? '32px' : '36px',
    boxSizing: 'border-box',
    margin: 0,
    padding: sm ? '0 28px 0 10px' : '0 28px 0 12px',
    borderRadius: '8px',
    border: sm ? '1px solid #e5e7eb' : '1px solid #e9ecef',
    backgroundColor: '#fff',
    fontFamily: sm ? '"Baikal Book"' : 'inherit',
    fontSize: sm ? '13px' : '14px',
    lineHeight: sm ? '30px' : '34px',
    color: '#111827',
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    backgroundImage: FILTER_CHEVRON,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    backgroundSize: '16px 16px',
  };
};

export const formatSchoolOption = (school) => {
  if (!school) return '';
  const location = [school.city, school.state].filter(Boolean).join(', ');
  const label = location ? `${school.name} - ${location}` : school.name;
  return school.is_active === false ? `${label} (Inactive)` : label;
};

export const renderSchoolLogo = (school, size = 28) => {
  const src = school?.image;
  if (src) {
    return (
      <img
        src={src}
        alt=""
        style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }}
      />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 6,
        backgroundColor: '#f3f4f6',
        flexShrink: 0,
      }}
    />
  );
};

const SchoolPicker = ({
  schools,
  value,
  onChange,
  placeholder = 'Please select a school',
  // Opt-in: a dropdown row that clears the school. Closed label stays
  // `placeholder` until the page sets allSelected.
  allOptionLabel = null,
  allSelected = false,
  // Closed control matches the selects beside it. Standalone pages stay the 56px pill.
  // sm: transactions toolbar (30px, 13px). md: analytics filters (35px, 14px).
  size = null,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);

  // Clicking anywhere else closes the menu — without this it stays open behind
  // whatever the admin does next.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    const rows = Array.isArray(schools) ? schools : [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((school) => formatSchoolOption(school).toLowerCase().includes(q));
  }, [schools, search]);

  const select = (school) => {
    setSearch('');
    setOpen(false);
    if (onChange) onChange(school);
  };

  const showingAll = !value && allSelected && allOptionLabel;
  const sized = size === 'sm' || size === 'md';
  const logoSize = size === 'sm' ? 16 : size === 'md' ? 18 : 24;

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'relative',
        maxWidth: size === 'sm' ? '220px' : size === 'md' ? '240px' : '480px',
        minWidth: sized ? '140px' : undefined,
        flex: sized ? '0 1 auto' : undefined,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: sized ? '6px' : '10px',
          width: '100%',
          boxSizing: 'border-box',
          margin: 0,
          appearance: sized ? 'none' : undefined,
          WebkitAppearance: sized ? 'none' : undefined,
          height: size === 'sm' ? '32px' : size === 'md' ? '36px' : '56px',
          padding: size === 'sm' ? '0 8px 0 10px' : size === 'md' ? '0 10px 0 12px' : '0 14px',
          background: '#fff',
          border: size === 'sm' ? '1px solid #e5e7eb' : size === 'md' ? '1px solid #e9ecef' : '1px solid #ebebeb',
          borderRadius: sized ? '8px' : '12px',
          cursor: 'pointer',
          fontFamily: size === 'md' ? 'inherit' : '"Baikal Book"',
          fontSize: size === 'sm' ? '13px' : size === 'md' ? '14px' : '15px',
          lineHeight: 1,
        }}
      >
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: sized ? '6px' : '10px',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          {sized ? null : <MagnifierIcon />}
          {showingAll ? null : value ? renderSchoolLogo(value, logoSize) : null}
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: value || showingAll ? '#000' : '#c0c0c0',
            }}
          >
            {formatSchoolOption(value) || (showingAll ? allOptionLabel : placeholder)}
          </span>
        </span>
        <span
          aria-hidden="true"
          style={{
            display: 'flex',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s ease',
          }}
        >
          <svg width={sized ? 16 : 18} height={sized ? 16 : 18} viewBox="0 0 24 24" fill="none">
            <path
              d="M7 10L12 15L17 10"
              stroke="#C0C0C0"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 40,
            backgroundColor: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: '12px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
          }}
        >
          <div style={{ borderBottom: '1px solid #ebebeb' }}>
            <div style={searchBox}>
              <span style={searchIcon} aria-hidden="true">
                <MagnifierIcon />
              </span>
              <input
                type="search"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Start typing to search"
                className="school-search-input"
                style={searchInput}
              />
            </div>
          </div>
          <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {allOptionLabel && !search.trim() ? (
              <button
                type="button"
                onClick={() => select(null)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  borderBottom: '1px solid #f3f4f6',
                  backgroundColor: !value && allSelected ? '#eff6ff' : 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  color: '#111827',
                }}
              >
                {allOptionLabel}
              </button>
            ) : null}
            {filtered.length === 0 ? (
              <div
                style={{
                  padding: '12px',
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  color: '#6b7280',
                }}
              >
                No schools found
              </div>
            ) : (
              filtered.map((school) => {
                const isSelected = value?.id === school.id;
                const inactive = school.is_active === false;
                return (
                  <button
                    key={school.id}
                    type="button"
                    onClick={() => select(school)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: 'none',
                      borderBottom: '1px solid #f3f4f6',
                      backgroundColor: isSelected ? '#eff6ff' : 'white',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      color: inactive ? '#6b7280' : '#111827',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    {renderSchoolLogo(school, 28)}
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatSchoolOption(school)}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolPicker;
